import { ChevronDown, ChevronRight } from 'react-feather';
import { AA_BUILDING_TYPES } from '../../pricing/engineeringQuote';

const MODIFIER_LABELS = {
  rush: 'RUSH +50%',
  priority: 'PRIORITY +25%',
  seismic_special_area: 'Seismic +25%/+40%',
  residential: 'Residential ×1.5',
};

export default function OrderEngineeringSection({
  sections,
  toggleSection,
  engineeringInput,
  onEngineeringInputChange,
  engineeringQuote,
}) {
  const { buildingType, wantsCalculations, isRush, isPriority, isResidential, isLoafingShed, enabled } =
    engineeringInput;

  const selectedBt = AA_BUILDING_TYPES.find((bt) => bt.value === buildingType);
  const showLoafing = selectedBt?.isLoafingShedEligible;

  function set(field, value) {
    onEngineeringInputChange({ ...engineeringInput, [field]: value });
  }

  const fmt = (n) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

  return (
    <div className="tab-information">
      <div className="tab-information-block">
        <div
          className="title-block"
          onClick={(e) => toggleSection('engineeringTab', e)}
        >
          <h2 className="title-h2">Engineering Plans</h2>
          <button>
            {sections.engineeringTab ? <ChevronDown size={24} /> : <ChevronRight size={24} />}
          </button>
        </div>

        {sections.engineeringTab && (
          <div className="p-4 space-y-4">
            {/* Enable toggle */}
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="w-4 h-4"
                checked={enabled}
                onChange={(e) => set('enabled', e.target.checked)}
              />
              <span className="text-sm font-medium text-slate-700">Include engineering plans in quote</span>
            </label>

            {enabled && (
              <>
                {/* Building type */}
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                    Building Type
                  </label>
                  <select
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                    value={buildingType}
                    onChange={(e) => set('buildingType', e.target.value)}
                  >
                    <option value="">— select —</option>
                    {AA_BUILDING_TYPES.map((bt) => (
                      <option key={bt.value} value={bt.value}>
                        {bt.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Options row */}
                <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                  <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
                    <input
                      type="checkbox"
                      className="w-4 h-4"
                      checked={wantsCalculations}
                      onChange={(e) => set('wantsCalculations', e.target.checked)}
                    />
                    Include calculations
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
                    <input
                      type="checkbox"
                      className="w-4 h-4"
                      checked={isRush}
                      onChange={(e) => set('isRush', e.target.checked)}
                    />
                    RUSH (+50% drawings)
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
                    <input
                      type="checkbox"
                      className="w-4 h-4"
                      checked={isPriority}
                      onChange={(e) => set('isPriority', e.target.checked)}
                    />
                    PRIORITY (+25% drawings)
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
                    <input
                      type="checkbox"
                      className="w-4 h-4"
                      checked={isResidential}
                      onChange={(e) => set('isResidential', e.target.checked)}
                    />
                    Residential (×1.5)
                  </label>

                  {showLoafing && (
                    <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
                      <input
                        type="checkbox"
                        className="w-4 h-4"
                        checked={isLoafingShed}
                        onChange={(e) => set('isLoafingShed', e.target.checked)}
                      />
                      Loafing shed (+$250)
                    </label>
                  )}
                </div>

                {/* Quote result */}
                {engineeringQuote && !engineeringQuote.error && buildingType && (
                  <div className="bg-slate-50 rounded-xl p-4 space-y-2 text-sm">
                    {engineeringQuote.dimensionWarning && (
                      <p className="text-amber-600 text-xs">
                        ⚠ Dimensions rounded up to nearest matrix values (
                        {engineeringQuote.snappedWidth}′ × {engineeringQuote.snappedLength}′)
                      </p>
                    )}

                    <div className="flex justify-between text-slate-600">
                      <span>Drawings</span>
                      <span className="font-medium">{fmt(engineeringQuote.drawingsTotal)}</span>
                    </div>

                    {engineeringQuote.calculationsTotal > 0 && (
                      <div className="flex justify-between text-slate-600">
                        <span>
                          Calculations
                          {engineeringQuote.forcedCalculations && (
                            <span className="ml-1 text-xs text-blue-500">(seismic required)</span>
                          )}
                        </span>
                        <span className="font-medium">{fmt(engineeringQuote.calculationsTotal)}</span>
                      </div>
                    )}

                    {engineeringQuote.appliedModifiers.length > 0 && (
                      <p className="text-xs text-slate-400">
                        Applied: {engineeringQuote.appliedModifiers.map((m) => MODIFIER_LABELS[m] ?? m).join(', ')}
                      </p>
                    )}

                    <div className="border-t border-slate-200 pt-2 flex justify-between font-semibold text-slate-800">
                      <span>Engineering Quote</span>
                      <span className="text-blue-600">{fmt(engineeringQuote.total)}</span>
                    </div>
                  </div>
                )}

                {engineeringQuote?.error && (
                  <p className="text-xs text-red-500 bg-red-50 rounded-lg p-3">{engineeringQuote.error}</p>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
